/**
 * Needle helpers for scrolling/highlighting a SourceCard markdown preview to the
 * citation excerpt shown under "View Details".
 *
 * Mirrors the View Details render: ``sources[i].content?.replace('```json ', '')``.
 */

/** Full View Details text (after the same `` ```json `` strip the UI uses). */
export function citationViewDetailsText(content: string | undefined | null): string {
  if (!content) return '';
  // View Details only strips this exact prefix; also clear common fence variants.
  let displayed = content.replace('```json ', '');
  displayed = displayed
    .replace(/^```(?:json|markdown|md|text)?\s*/i, '')
    .replace(/```\s*$/i, '')
    .trim();
  return displayed;
}

/**
 * First ``maxLen`` characters of the View Details text — used to locate the
 * matching section in a (possibly large) markdown file.
 */
export function citationScrollNeedle(
  content: string | undefined | null,
  maxLen = 100,
): string {
  const displayed = citationViewDetailsText(content);
  if (!displayed) return '';
  return displayed.slice(0, Math.max(0, maxLen));
}

/** Collapse whitespace for resilient excerpt ↔ file matching. */
export function normalizeCitationSearchText(text: string): string {
  return text
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/\u2026/g, '...')
    .replace(/\u00A0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * Strip common markdown markers so citation source text can match rendered
 * ``textContent`` (and raw source after the same strip).
 *
 * Avoid single-``_`` emphasis stripping — it destroys ``snake_case`` tokens that
 * often appear in evidence filenames and body text.
 */
export function stripMarkdownForSearch(text: string): string {
  const stripped = text
    .replace(/```[\w]*\n?/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[[^\]]*]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)]\([^)]*\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/(\*\*|__)([\s\S]*?)\1/g, '$2')
    // Asterisk emphasis only (not `_…_`) — lookbehind-free for wider runtimes.
    .replace(/(^|[^\w*])\*([^*\n]+)\*([^\w*]|$)/g, '$1$2$3')
    .replace(/^>\s?/gm, '')
    .replace(/^[-*+]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '')
    // Underscores → spaces so snake_case still tokenizes for word-window search.
    .replace(/_/g, ' ')
    .replace(/[*~`#>|[\]()]/g, ' ');
  return normalizeCitationSearchText(stripped);
}

/**
 * Build progressively shorter search needles from a citation excerpt.
 *
 * Includes sliding word-windows (not just prefixes) so a leading preamble that
 * is absent from the .md file does not force a no-match / top-of-file fallback.
 */
export function citationSearchNeedles(needle: string): string[] {
  const raw = normalizeCitationSearchText(needle);
  const plain = stripMarkdownForSearch(needle);
  const out: string[] = [];
  const push = (value: string) => {
    if (value && value.length >= 8 && !out.includes(value)) out.push(value);
  };

  push(plain);
  push(raw);

  for (const base of [plain, raw]) {
    if (!base) continue;
    for (let len = Math.min(base.length, 100); len >= 12; len -= 8) {
      push(base.slice(0, len));
    }
    const words = base.split(' ').filter(Boolean);
    // Longest word-windows first; slide across the excerpt (not only index 0).
    for (let count = Math.min(12, words.length); count >= 3; count -= 1) {
      for (let start = 0; start + count <= words.length; start += 1) {
        const window = words.slice(start, start + count).join(' ');
        // Keep short windows from flooding with weak 3-word matches.
        if (count <= 3 && window.length < 14) continue;
        push(window);
      }
    }
  }
  return out;
}

function blockIndexContainingNormalizedOffset(
  plainParts: string[],
  at: number,
): number {
  let cursor = 0;
  for (let i = 0; i < plainParts.length; i++) {
    const part = plainParts[i];
    const next = cursor + part.length + (i < plainParts.length - 1 ? 1 : 0);
    if (at < next) return i;
    cursor = next;
  }
  return -1;
}

/**
 * Find the first block whose text contains a citation needle.
 * Tries markdown-stripped and raw normalized forms with progressive shortening
 * and sliding word-windows. Also searches the joined document so matches that
 * span block splits still resolve.
 */
export function findBlockIndexForCitationNeedle(
  blocks: ReadonlyArray<{ content: string }>,
  needle: string,
): number {
  const needles = citationSearchNeedles(needle);
  if (!needles.length || !blocks.length) return -1;

  const plainParts = blocks.map((b) => stripMarkdownForSearch(b.content));
  const rawParts = blocks.map((b) => normalizeCitationSearchText(b.content));

  for (const n of needles) {
    for (let i = 0; i < blocks.length; i++) {
      if (plainParts[i].includes(n) || rawParts[i].includes(n)) return i;
    }
  }

  // Span-aware fallback: locate needle in the full document, then map to a block.
  const joinedPlain = plainParts.join(' ');
  const joinedRaw = rawParts.join(' ');
  for (const n of needles) {
    let at = joinedPlain.indexOf(n);
    if (at >= 0) {
      const idx = blockIndexContainingNormalizedOffset(plainParts, at);
      if (idx >= 0) return idx;
    }
    at = joinedRaw.indexOf(n);
    if (at >= 0) {
      const idx = blockIndexContainingNormalizedOffset(rawParts, at);
      if (idx >= 0) return idx;
    }
  }
  return -1;
}

export const CITATION_BEGIN_MARKER_ATTR = 'data-citation-scroll-match';
export const CITATION_BEGIN_MARKER_TEXT = 'BEGINNING OF CITATION';

type TextNodeSlice = {
  node: Text;
  /** Absolute start offset of this node in the concatenated raw text. */
  rawStart: number;
};

/**
 * Build concatenated text from ``root`` plus a parallel normalized string and a
 * map from each normalized character index → absolute raw character index.
 */
function buildNormalizedTextMap(root: HTMLElement): {
  nodes: TextNodeSlice[];
  rawConcat: string;
  normalized: string;
  normToRaw: number[];
} | null {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      if (!node.nodeValue) return NodeFilter.FILTER_REJECT;
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      // Attribute presence, not ``="true"``: the viewer's top-of-file fallback bar
      // carries a different value, and its own "BEGINNING OF CITATION" text must
      // never become part of the searchable document text.
      if (parent.closest(`[${CITATION_BEGIN_MARKER_ATTR}]`)) {
        return NodeFilter.FILTER_REJECT;
      }
      const tag = parent.closest('script, style');
      if (tag) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const nodes: TextNodeSlice[] = [];
  let rawConcat = '';
  let normalized = '';
  const normToRaw: number[] = [];

  let current: Node | null;
  while ((current = walker.nextNode())) {
    const textNode = current as Text;
    const value = textNode.nodeValue ?? '';
    // Insert a synthetic space between adjacent block/inline text nodes so
    // ``<h2>Findings</h2><p>The…`` matches ``findings the…``.
    if (
      rawConcat.length > 0 &&
      normalized.length > 0 &&
      normalized[normalized.length - 1] !== ' ' &&
      value.length > 0 &&
      !/^\s/.test(value)
    ) {
      normToRaw.push(rawConcat.length);
      normalized += ' ';
    }
    nodes.push({ node: textNode, rawStart: rawConcat.length });

    for (let i = 0; i < value.length; i++) {
      const ch = value[i];
      const rawIndex = rawConcat.length + i;
      if (/\s/.test(ch)) {
        if (normalized.length === 0 || normalized[normalized.length - 1] !== ' ') {
          normToRaw.push(rawIndex);
          normalized += ' ';
        }
      } else {
        normToRaw.push(rawIndex);
        normalized += ch.toLowerCase();
      }
    }
    rawConcat += value;
  }

  // Trim leading/trailing normalized spaces and keep normToRaw aligned.
  let start = 0;
  let end = normalized.length;
  while (start < end && normalized[start] === ' ') start += 1;
  while (end > start && normalized[end - 1] === ' ') end -= 1;
  if (start === 0 && end === normalized.length) {
    return { nodes, rawConcat, normalized, normToRaw };
  }
  return {
    nodes,
    rawConcat,
    normalized: normalized.slice(start, end),
    normToRaw: normToRaw.slice(start, end),
  };
}

function resolveRawPoint(
  nodes: TextNodeSlice[],
  rawIndex: number,
): { node: Text; offset: number } | null {
  for (let i = 0; i < nodes.length; i++) {
    const { node, rawStart } = nodes[i];
    const len = node.nodeValue?.length ?? 0;
    const rawEnd = rawStart + len;
    if (rawIndex < rawEnd || (rawIndex === rawEnd && i === nodes.length - 1)) {
      return { node, offset: Math.min(Math.max(0, rawIndex - rawStart), len) };
    }
  }
  return null;
}

/** Inline / phrasing tags the citation bar must never be nested inside. */
const CITATION_MARKER_INLINE_TAGS = new Set([
  'A',
  'SPAN',
  'STRONG',
  'EM',
  'B',
  'I',
  'U',
  'CODE',
  'MARK',
  'SMALL',
  'SUB',
  'SUP',
  'ABBR',
  'TIME',
  'DATA',
  'LABEL',
  'BUTTON',
  'SVG',
  'PATH',
]);

const CITATION_MARKER_BLOCK_SELECTOR =
  'p, li, h1, h2, h3, h4, h5, h6, blockquote, pre, table, ul, ol, section, article, div';

/** Yellow bar inserted at the citation match (or top-of-file fallback). */
export function createCitationBeginMarker(): HTMLElement {
  const marker = document.createElement('div');
  marker.setAttribute(CITATION_BEGIN_MARKER_ATTR, 'true');
  marker.setAttribute('data-testid', 'citation-begin-marker');
  marker.setAttribute('role', 'note');
  marker.setAttribute('contenteditable', 'false');
  // Keep the bar visually/behaviorally separate from surrounding markdown chrome.
  marker.className =
    'my-3 box-border block w-full rounded-sm border-2 border-yellow-400 bg-yellow-300 px-3 py-2 text-center text-sm font-semibold tracking-wide text-black no-underline pointer-events-none';
  marker.textContent = CITATION_BEGIN_MARKER_TEXT;
  return marker;
}

/**
 * Resolve a block-level sibling insertion point for the citation bar.
 * Never returns an inline host (links, emphasis, etc.) so the bar cannot become
 * part of an ``<a>`` or other phrasing content.
 */
export function resolveCitationMarkerInsertBefore(
  textNode: Text,
  root: HTMLElement,
): HTMLElement | null {
  let node: HTMLElement | null = textNode.parentElement;
  if (!node || !root.contains(node)) return null;

  // Climb out of any inline / link ancestors first.
  while (node && node !== root && CITATION_MARKER_INLINE_TAGS.has(node.tagName)) {
    node = node.parentElement;
  }
  if (!node || node === root || !root.contains(node)) return null;

  // Prefer the nearest semantic block still inside root.
  const block = node.closest(CITATION_MARKER_BLOCK_SELECTOR);
  let anchor =
    block instanceof HTMLElement && root.contains(block) && block !== root
      ? block
      : node;

  // If we somehow still sit under a link (invalid HTML), promote past it.
  const enclosingLink = anchor.closest('a');
  if (enclosingLink instanceof HTMLElement && root.contains(enclosingLink)) {
    const blockOutsideLink = enclosingLink.closest(CITATION_MARKER_BLOCK_SELECTOR);
    if (
      blockOutsideLink instanceof HTMLElement &&
      root.contains(blockOutsideLink) &&
      blockOutsideLink !== root &&
      !blockOutsideLink.closest('a')
    ) {
      anchor = blockOutsideLink;
    } else {
      anchor = enclosingLink;
    }
  }

  // Final guard: insertion parent must not be a link / inline tag.
  let parent = anchor.parentElement;
  while (
    parent &&
    parent !== root &&
    CITATION_MARKER_INLINE_TAGS.has(parent.tagName)
  ) {
    anchor = parent;
    parent = parent.parentElement;
  }
  if (!parent || !root.contains(parent)) return null;
  if (CITATION_MARKER_INLINE_TAGS.has(parent.tagName)) return null;

  return anchor;
}

/**
 * Insert a "BEGINNING OF CITATION" bar immediately before the block that
 * contains the first match of ``needle``. Always a block sibling — never nested
 * inside links or other inline markdown elements.
 */
export function insertCitationBeginMarker(
  root: HTMLElement,
  needle: string,
): HTMLElement | null {
  const existing = root.querySelector(`[${CITATION_BEGIN_MARKER_ATTR}="true"]`);
  if (existing instanceof HTMLElement) return existing;

  const needles = citationSearchNeedles(needle);
  if (!needles.length) return null;

  const map = buildNormalizedTextMap(root);
  if (!map || !map.normalized) return null;

  let matchStart = -1;
  for (const n of needles) {
    const idx = map.normalized.indexOf(n);
    if (idx >= 0) {
      matchStart = idx;
      break;
    }
  }
  if (matchStart < 0) return null;

  const rawStart = map.normToRaw[matchStart];
  if (rawStart == null) return null;

  const startPoint = resolveRawPoint(map.nodes, rawStart);
  if (!startPoint) return null;

  const insertBefore = resolveCitationMarkerInsertBefore(startPoint.node, root);
  if (!insertBefore?.parentElement) return null;

  const marker = createCitationBeginMarker();
  insertBefore.parentElement.insertBefore(marker, insertBefore);

  // Belt-and-suspenders: if a browser still left us inside a link, hoist out.
  const parentLink = marker.closest('a');
  if (parentLink && root.contains(parentLink) && parentLink.parentElement) {
    parentLink.parentElement.insertBefore(marker, parentLink);
  }
  return marker;
}
